/**
 * aiLookupService.ts — Spoke-side AI lookup API client.
 *
 * Calls the /api/ai-lookup/* routes on the backend.
 * Uses the existing `api` axios instance (includes auth token interceptor).
 */

import api from './api';

export interface PartWarranty {
  months: number | null;
  miles: number | null;
  type: 'limited' | 'lifetime' | 'unlimited' | 'manufacturer' | 'none' | 'unknown';
  notes?: string;
}

export interface AiPartsOption {
  brand: string;
  part_number?: string;
  vendor?: string;
  price_cents?: number;
  fitment_status?: string;
  fitment_notes?: string | null;
  oem_or_aftermarket?: string;
  source_url?: string | null;
  [key: string]: unknown;
}

export interface AiPartsResult {
  options: AiPartsOption[];
  excluded_options_count?: number;
  excluded_reasons?: string[];
  vehicle_attributes_used?: Record<string, unknown>;
}

export interface AiRecallItem {
  component: string;
  summary: string;
  remedy: string;
}

export interface AiTsbItem {
  id: string;
  description: string;
  applies_to: string;
}

export interface AiFixHit {
  symptom: string;
  cause: string;
  correction: string;
  mileage_band: string;
  dtc_codes: string;
  outcome: string;
  _score: number;
}

export interface AiLookupResponse<T> {
  lookupId: string;
  result: T;
}

/** Result from the licensed lookup endpoint */
export interface AiLicensedResult {
  kind: string;
  value: string;
  source: 'alldata' | 'autozone_pro';
  poolable: false;
  notes?: string;
}

export interface AiLicensedLookupResponse {
  lookupId: string;
  result: AiLicensedResult;
  /** Provenance note from the server — always present to make privacy explicit */
  provenance: string;
}

/**
 * POST /api/ai-lookup/parts
 */
async function parts(lineItemId: number, partQuery: string): Promise<AiLookupResponse<AiPartsResult | unknown>> {
  const res = await api.post('/ai-lookup/parts', { lineItemId, partQuery });
  return res.data;
}

/**
 * POST /api/ai-lookup/recalls
 */
async function recalls(lineItemId: number): Promise<AiLookupResponse<AiRecallItem[]>> {
  const res = await api.post('/ai-lookup/recalls', { lineItemId });
  return res.data;
}

/**
 * POST /api/ai-lookup/tsbs
 */
async function tsbs(lineItemId: number): Promise<AiLookupResponse<AiTsbItem[]>> {
  const res = await api.post('/ai-lookup/tsbs', { lineItemId });
  return res.data;
}

/**
 * POST /api/ai-lookup/similar-fixes
 */
async function similarFixes(lineItemId: number): Promise<AiLookupResponse<AiFixHit[]>> {
  const res = await api.post('/ai-lookup/similar-fixes', { lineItemId });
  return res.data;
}

/**
 * POST /api/ai-lookup/licensed
 * Calls the shop's own vendor / AutoZone Pro subscription.
 * Results are marked poolable:false and never shared.
 */
async function licensed(
  lineItemId: number,
  vendor: 'alldata' | 'autozone_pro',
  query: string
): Promise<AiLicensedLookupResponse> {
  const res = await api.post('/ai-lookup/licensed', { lineItemId, vendor, query });
  return res.data;
}

/**
 * PUT /api/ai-lookup/parts/options/:optionId/warranty
 * Tech override for AI-extracted warranty on a part_options row.
 * Pass null to clear the override.
 */
async function updateWarranty(
  optionId: number,
  warranty: PartWarranty | null
): Promise<{ ok: true; warranty: PartWarranty | null }> {
  const res = await api.put(
    `/ai-lookup/parts/options/${optionId}/warranty`,
    { warranty }
  );
  return res.data;
}

export const aiLookupService = {
  parts,
  recalls,
  tsbs,
  similarFixes,
  licensed,
  updateWarranty,
};
